// File: src/pages/sakshamshram/SakshamShramInsights.jsx
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";

/**
 * SakshamShramInsights.jsx
 * - Mobile-first, responsive Tailwind layout
 * - Tabs for Stakeholders / USPs
 * - Search + Reset
 * - Expand / Collapse per stakeholder
 * - Header (BlueNext) integrated as requested
 */

/* ---------- Data (as provided) ---------- */
const STAKEHOLDERS = [
  {
    id: "blueWorkers",
    label: "Blue Collar Workers",
    emoji: "👷‍♂️",
    pains: [
      "Limited access to digital learning and skill-building platforms.",
      "Difficulty in finding reliable employment opportunities and gigs.",
      "Low awareness of productivity and task management tools.",
      "Limited financial literacy and difficulty tracking earnings.",
      "Challenges in balancing work, upskilling, and personal growth."
    ],
    opportunities: [
      "Access to immersive digital learning and workforce workshops.",
      "Job boards and global opportunities specifically for blue collar roles.",
      "Task & workflow management tools like diaries, goal boards, and Kanban.",
      "Digital income tools such as ValueShare, Rewarding Ads, and Shram Boutique.",
      "Self-reflection and skill-tracking tools like My Memories, Focus Timer, and IKIGAI.",
      "Community & social engagement via Shram Forum and Green Contribution programs."
    ]
  },
  {
    id: "employers",
    label: "Employers & Contractors",
    emoji: "🏗️",
    pains: [
      "Difficulty finding skilled blue collar workers reliably.",
      "High employee turnover and skill mismatch.",
      "Limited tools to track performance and attendance digitally."
    ],
    opportunities: [
      "Access verified workforce profiles with skills and availability.",
      "Digital attendance and performance tracking dashboards.",
      "Engagement in training and certification programs to upskill employees."
    ]
  },
  {
    id: "trainingPartners",
    label: "Training & Upskilling Partners",
    emoji: "🎓",
    pains: [
      "Limited visibility of blue collar workforce needing upskilling.",
      "Challenges in delivering targeted skill development programs.",
      "Difficulty tracking learner engagement and impact."
    ],
    opportunities: [
      "Provide workshops, e-learning, and podcasts tailored to blue collar workers.",
      "Measure learning outcomes via dashboards and engagement metrics.",
      "Collaborate with Saksham Shram to certify and recognize skilled workers."
    ]
  },
  {
    id: "community",
    label: "Community & Peer Groups",
    emoji: "🤝",
    pains: [
      "Workers often lack a support network for guidance and motivation.",
      "Difficulty sharing knowledge and best practices within peer groups.",
      "Limited awareness of community events and recognition programs."
    ],
    opportunities: [
      "Participate in forums, social events, and peer-to-peer learning.",
      "Engage in community reward programs for contributions and skills.",
      "Build reputation and recognition via Shram Applause and Innovation programs."
    ]
  },
  {
    id: "government",
    label: "Government & Policy Makers",
    emoji: "🏛️",
    pains: [
      "Lack of real-time data on blue collar employment and skill levels.",
      "Challenges in implementing skill development and employment schemes.",
      "Difficulty in measuring impact of worker-focused initiatives."
    ],
    opportunities: [
      "Dashboards provide measurable workforce metrics and KPIs.",
      "Support policy-making with insights from digital learning and job trends.",
      "Scale skill-building and employment programs with monitored outcomes."
    ]
  },
  {
    id: "corporates",
    label: "Corporate Partners & Sponsors",
    emoji: "🏢",
    pains: [
      "Difficulty sourcing skilled blue collar talent for projects.",
      "Limited tools to measure ROI on CSR initiatives targeting workforce development.",
      "Challenges in implementing large-scale workforce upskilling programs."
    ],
    opportunities: [
      "Integrate CSR initiatives with Saksham Shram for skill-building and employment.",
      "Track engagement, skill certifications, and worker outcomes.",
      "Facilitate collaboration for community, learning, and earning programs."
    ]
  }
];


const USPs = [
  {
    id: "digitalIncome",
    title: "Digital Income Opportunities",
    desc: "Empower women through ValueShare, Rewarding Ads, Rewarding Videos, and Shakti Digital Boutique for direct earnings.",
    emoji: "💰",
  },
  {
    id: "digitalLearning",
    title: "Digital Learning Hub",
    desc: "Access curated workshops, podcasts, books, Shakti Shorts, and interactive learning for continuous skill-building.",
    emoji: "💡",
  },
  {
    id: "selfReflection",
    title: "Self-Reflection Tools",
    desc: "Track personal growth with My Pink Card, Memories, Focus Timer, IKIGAI exercises, AI Counselling, and Journals.",
    emoji: "🪞",
  },
  {
    id: "shaktiShopping",
    title: "Shakti Shopping & Marketplace",
    desc: "Buy, sell, and explore products digitally with Window Shopping, Personal Shopping, Home Basket, and Digital Tryout.",
    emoji: "🛍️",
  },
  {
    id: "communityEngagement",
    title: "Community & Social Connect",
    desc: "Engage with peers, mentors, and farmers via Shakti Forum, Green Shakti, Applause, and BoloGrahak initiatives.",
    emoji: "🤝",
  },
  {
    id: "recognitionProgress",
    title: "Recognition & Progress Tracking",
    desc: "Celebrate achievements and track growth with Shakti Ranking, Innovations, BlueNext Database, and Gamified Challenges.",
    emoji: "🏅",
  },
  {
    id: "analyticsInsights",
    title: "Analytics & Insights",
    desc: "Gain actionable insights on learning, financial impact, and community engagement through dashboards and studies.",
    emoji: "📊",
  },
];

/* ---------- Small UI helpers ---------- */
const Pill = ({ children }) => (
  <span className="inline-block bg-blue-700/30 text-blue-100 px-2 py-0.5 rounded-full text-xs font-medium">
    {children}
  </span>
);

const ExpandIcon = ({ open }) => (
  <span className={`ml-2 text-sm ${open ? "rotate-180" : "rotate-0"} transition-transform`}>
    ▼
  </span>
);

/* ---------- Component ---------- */
export default function SakshamShramInsights() {
  const [tab, setTab] = useState("stakeholders");
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const filteredStakeholders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAKEHOLDERS;
    return STAKEHOLDERS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.pains.some((p) => p.toLowerCase().includes(q)) ||
        s.opportunities.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-blue-950 p-4 md:p-8 text-gray-100">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-blue-800 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 shadow-lg">
        <div className="flex flex-col">
          <div className="text-xl lg:text-2xl font-bold text-blue-400">🧢 BlueNext</div>
          <div className="text-[11px] lg:text-lg text-yellow-200">
            SAKSHAM Shram / Workforce: Skilled And Keen Systematic Hands Achieves More!
          </div>
        </div>

        <div className="text-[12px] lg:text-lg text-center my-1 mx-1 mt-2 text-blue-200/90">
          Extending Digital Empowerment for Blue Collar Literacy, Productivity and Growth!
        </div>

        <div className="mt-3 flex flex-wrap justify-center gap-3 text-sm text-blue-500">
          <Link to="/think5whysistrusted" className="text-yellow-200 hover:text-yellow-300 text-[12px] lg:text-lg">
            🔒 Trusted & Secure Platform – ISO 9001, ISO 27001, SOC-2 Certified
          </Link>
        </div>
      </header>

      {/* Page Header */}
      <div className="mx-auto mb-6 text-center">
        <h1 className="text-lg md:text-xl font-bold text-blue-400">SakshamShram & BlueNext Insights</h1>
        <p className="text-gray-300 mt-2 text-sm md:text-base">
          Empowering women through Digital Income, Learning, Self-Reflection, Community, and Recognition.
        </p>
      </div>

      {/* Tabs */}
      <nav className="max-w-3xl mx-auto grid grid-cols-2 gap-2 justify-center mb-4">
        <button
          onClick={() => { setTab("stakeholders"); setExpandedId(null); }}
          className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "stakeholders"
              ? "bg-blue-600 text-black shadow"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          👩‍💻 Stakeholders
        </button>

        <button
          onClick={() => { setTab("usps"); setExpandedId(null); }}
          className={`flex items-center justify-center gap-2 px-3 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "usps"
              ? "bg-blue-600 text-black shadow"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          💡 USPs
        </button>
      </nav>

      {/* Search / Reset */}
      <div className="max-w-3xl mx-auto flex flex-col md:flex-row gap-2 items-center mb-6">
        <input
          aria-label="Search stakeholders"
          placeholder="Search stakeholders, pain or opportunity..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="bg-gray-800 text-gray-200 placeholder-gray-400 px-3 py-2 rounded-md text-sm w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex gap-2">
          <button
            onClick={() => { setQuery(""); setExpandedId(null); }}
            className="text-sm w-24 bg-gray-700 px-3 py-2 rounded-md hover:bg-gray-600"
          >
            Reset
          </button>
          <button
            onClick={() => { setQuery(""); setTab("stakeholders"); setExpandedId(null); }}
            className="text-sm w-28 bg-blue-700 px-3 py-2 rounded-md hover:bg-blue-600 text-white"
          >
            Show All
          </button>
        </div>
      </div>

      <main className="max-w-6xl mx-auto space-y-6 pb-8">
        {/* Stakeholders */}
        {tab === "stakeholders" && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredStakeholders.map((s) => {
              const isOpen = expandedId === s.id;
              return (
                <article key={s.id} className="bg-gradient-to-br from-gray-800 to-blue-900/20 rounded-lg border border-blue-800/30 shadow-sm overflow-hidden">
                  <div className="p-4 flex items-start gap-3">
                    <div className="text-3xl">{s.emoji}</div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-lg font-semibold text-blue-300">{s.label}</h3>
                        <div className="flex items-center gap-2">
                          <Pill>{s.opportunities.length} opps</Pill>
                          <button
                            onClick={() => setExpandedId(isOpen ? null : s.id)}
                            className="ml-2 bg-blue-700/20 text-sm px-2 py-1 rounded"
                            aria-expanded={isOpen}
                          >
                            {isOpen ? "Collapse" : "Expand"} <ExpandIcon open={isOpen} />
                          </button>
                        </div>
                      </div>

                      <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                        <strong>Top Pains:</strong> {s.pains.slice(0,3).join(" • ")}
                      </p>

                      {isOpen && (
                        <div className="mt-3 space-y-3 text-sm text-gray-200">
                          <div>
                            <h4 className="font-medium text-blue-300">Pain Points</h4>
                            <ol className="list-decimal list-inside mt-1 space-y-1">
                              {s.pains.map((p, idx) => <li key={idx}>{p}</li>)}
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-medium text-blue-300">Opportunities</h4>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              {s.opportunities.map((o, idx) => <li key={idx}>{o}</li>)}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
            {filteredStakeholders.length === 0 && (
              <div className="bg-gray-800 p-4 rounded text-center text-gray-300">No results found for your search.</div>
            )}
          </section>
        )}

        {/* USPs */}
        {tab === "usps" && (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USPs.map((u) => (
              <div key={u.id} className="bg-gradient-to-br from-blue-700/10 to-gray-800 rounded-lg p-4 border border-blue-600/10">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{u.emoji}</div>
                  <div>
                    <h3 className="font-semibold text-lg text-blue-300">{u.title}</h3>
                    <p className="text-sm text-gray-200 mt-2">{u.desc}</p>
                    <div className="mt-3">
                      <Link to="/contact" className="text-sm text-blue-300 hover:text-blue-200">
                        Learn how to integrate this → 
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="max-w-6xl mx-auto mt-8 text-center text-sm text-gray-400">
        SakshamShram & BlueNext • Empowering women with Digital Income, Learning, and Recognition 🌸
      </footer>
    </div>
  );
}
